import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2, Hourglass, Info } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Form } from '@/components/molecules/form-field';
import { RetentionInfoModal } from '@/components/molecules/retention-info-modal';
import { FormPageHeader, FormProgressIndicator } from '@/components/forms';
import { LimitReachedAlert, LimitIndicator } from '@/lib/subscription';

import {
  ProductBasicInfo,
  ProductPricing,
  ProductAttributes,
  ProductWeight,
  ProductTags,
  ProductImages,
  useProductImages,
  useProductEarnings,
  useProductFormProgress,
  productsService,
  createProductSchema,
  type CreateProductFormData,
} from '@/features/products';

import { useShops } from '@/features/shops/hooks/use-shops';
import { useCategories } from '@/lib/hooks/use-categories';
import { parseStringArray } from '@/lib/utils';
import { useCategoryRequirements } from '@/lib/hooks/use-category-requirements';
import { useQueryClient } from '@tanstack/react-query';
import { useCanCreateProduct, useSubscriptionLimits } from '@/lib/subscription';

export default function NewProductPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRetentionModal, setShowRetentionModal] = useState(false);

  const { data: shops, isLoading: isLoadingShops } = useShops();
  const { data: categories, isLoading: isLoadingCategories } = useCategories();

  // Subscription gating
  const productAccess = useCanCreateProduct();
  const limits = useSubscriptionLimits();

  // Image handling with cropping support
  const {
    images,
    imagePreviews,
    isCompressing,
    handleAddImages,
    handleRemoveImage,
    pendingCropImage,
    cropQueue,
    handleCropComplete,
    handleCropCancel,
  } = useProductImages({ enableCropping: true });

  const form = useForm<CreateProductFormData>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      shopId: '',
      categoryId: '',
      name: '',
      description: '',
      price: 0,
      discountPrice: undefined,
      quantity: 1,
      currency: 'european_currency',
      unit: 'sold_individually',
      gender: undefined,
      sizes: '',
      tags: '',
      weight: 0,
      weightUnit: 'grams',
    },
  });

  // Category requirements
  const watchCategory = form.watch('categoryId');
  const selectedCategory = categories?.find((c) => String(c.id) === watchCategory);
  const requirements = useCategoryRequirements(selectedCategory?.name);

  // Auto-clear fields when category changes
  useEffect(() => {
    if (!requirements.showGender) form.setValue('gender', undefined);
    if (!requirements.showSizes) form.setValue('sizes', '');
  }, [requirements, form]);

  // Form values for progress and earnings
  const formValues = form.watch();

  // Earnings estimation
  const earningsEstimation = useProductEarnings({
    price: formValues.price,
    discountPrice: formValues.discountPrice,
  });

  // Form progress
  const { completedFields, totalFields } = useProductFormProgress({
    formValues,
    imageCount: images.length,
    requirements,
    mode: 'create',
  });

  const onSubmit = async (data: CreateProductFormData) => {
    if (images.length === 0) {
      toast.error(t('products.imagesRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const sizesArray = parseStringArray(data.sizes);

      await productsService.createProduct(
        {
          name: data.name,
          description: data.description,
          price: data.price,
          discountPrice: data.discountPrice,
          quantity: data.quantity,
          currency: data.currency,
          unit: data.unit,
          gender: data.gender,
          sizes: sizesArray,
          categories: [data.categoryId],
          tags: parseStringArray(data.tags),
          weight: data.weight,
          weight_unit: data.weightUnit,
          shopId: data.shopId,
        },
        images
      );

      toast.success(t('products.productCreated'));
      queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate('/products');
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Limit reached view
  if (!productAccess.canCreate) {
    return (
      <div className="pb-6">
        <FormPageHeader
          backLink="/products"
          title={t('products.addProduct')}
          description={t('products.addProductDescription')}
        />
        <LimitReachedAlert
          type="product"
          limit={productAccess.limit}
          onUpgrade={() => navigate('/settings/subscription')}
        />
      </div>
    );
  }

  // Build image previews for the component
  const imagePreviewsData = imagePreviews.map((src, index) => ({
    src,
    id: `new-${index}`,
  }));

  // Additional info for progress
  const progressInfo = selectedCategory
    ? `${t('products.category')}: ${t(`categories.${selectedCategory.name}`, { defaultValue: selectedCategory.name })}${requirements.showGender ? ` • ${t('products.genderRequired')}` : ''}${requirements.showSizes ? ` • ${t('products.sizesAvailable')}` : ''}`
    : undefined;

  return (
    <div className="pb-6">
      <FormPageHeader
        backLink="/products"
        title={t('products.addProduct')}
        description={t('products.addProductDescription')}
      />

      <div className="mb-6">
        <LimitIndicator
          current={limits.products.current}
          max={limits.products.limit}
          label={t('subscription.features.products')}
        />
      </div>

      <FormProgressIndicator
        completed={completedFields}
        total={totalFields}
        label={t('products.fieldsCompleted')}
        additionalInfo={progressInfo}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
            {/* Images - first on mobile */}
            <div className="order-first lg:order-last space-y-6">
              <ProductImages
                previews={imagePreviewsData}
                onAdd={handleAddImages}
                onRemove={(index) => handleRemoveImage(index)}
                isCompressing={isCompressing}
                pendingCropImage={pendingCropImage}
                cropQueueLength={cropQueue.length}
                onCropComplete={handleCropComplete}
                onCropCancel={handleCropCancel}
              />

              <div className="hidden lg:block sticky bottom-4">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('products.createProduct')}
                </Button>
              </div>
            </div>

            {/* Form fields */}
            <div className="lg:col-span-2 space-y-6">
              <ProductBasicInfo
                form={form}
                categories={categories}
                shops={shops}
                isLoadingCategories={isLoadingCategories}
                isLoadingShops={isLoadingShops}
                showShopSelect
                headerAction={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRetentionModal(true)}
                    className="flex items-center gap-2"
                  >
                    <Hourglass className="size-4" />
                    <span className="hidden sm:inline">{t('products.paymentRetention', 'Rétention paiement')}</span>
                    <Info className="size-4 sm:hidden" />
                  </Button>
                }
              />

              <ProductPricing
                form={form}
                showDiscountPrice
                earningsEstimation={earningsEstimation}
              />

              <ProductAttributes
                form={form}
                showGender={requirements.showGender}
                showSizes={requirements.showSizes}
              />

              <ProductWeight form={form} />

              <ProductTags form={form} />

              {/* Submit button - visible on mobile only */}
              <div className="lg:hidden">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('products.createProduct')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>

      <RetentionInfoModal
        open={showRetentionModal}
        onOpenChange={setShowRetentionModal}
        retentionDays={7}
        categoryName={selectedCategory ? t(`categories.${selectedCategory.name}`, { defaultValue: selectedCategory.name }) : undefined}
        context="creation"
      />
    </div>
  );
}
